import { useEffect, useRef } from 'react';
import { useVersion } from 'react-admin';

/**
 * Hook that executes a callback when react-admin's refresh is triggered.
 *
 * BACKWARDS COMPAT: Required while using custom containers instead of
 * react-admin's built-in data hooks. Listens to useVersion() changes
 * triggered by the toolbar refresh button.
 *
 * @param callback - Function to execute on refresh (not on initial render)
 * @param deps - Additional dependencies that should also trigger the callback
 *
 * @example
 * // In a form component with lazy-loaded autocomplete
 * useRefreshTrigger(() => {
 *   loadBuilderOptions('');
 *   loadRegionOptions('');
 * });
 *
 * @example
 * // With additional dependencies
 * useRefreshTrigger(() => loadSupervisors(regionId), [regionId]);
 */
export const useRefreshTrigger = (
  callback: () => void | Promise<void>,
  deps: React.DependencyList = [],
): void => {
  const version = useVersion();
  const isFirstRender = useRef(true);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    callbackRef.current();
  }, [version, ...deps]);
};

export default useRefreshTrigger;
